import Phaser from "phaser";
import { DIRECTIONS } from "../../constants/game";
import { Intersection, Point } from "../../types/game";

/**
 * MovementManager
 * Handles all Pacman movement logic and direction changes
 */
export class MovementManager {
  private scene: Phaser.Scene;
  private pacman: Phaser.Physics.Arcade.Sprite;
  private blockSize: number;
  private speed: number;
  private direction: string;
  private previousDirection: string;
  private nextIntersection: Point | null = null;
  private hasRespawned: boolean = false;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private getIntersections!: () => Intersection[];
  private isActive: boolean = false;

  constructor(
    scene: Phaser.Scene,
    pacman: Phaser.Physics.Arcade.Sprite,
    blockSize: number,
    speed: number,
    initialDirection: string,
    initialPreviousDirection: string
  ) {
    this.scene = scene;
    this.pacman = pacman;
    this.blockSize = blockSize;
    this.speed = speed;
    this.direction = initialDirection;
    this.previousDirection = initialPreviousDirection;
    this.isActive = false;
  }

  /**
   * Set references needed for movement
   */
  setReferences(
    cursors: Phaser.Types.Input.Keyboard.CursorKeys,
    getIntersections: () => Intersection[]
  ): void {
    this.cursors = cursors;
    this.getIntersections = getIntersections;
  }

  /**
   * Get current direction
   */
  getDirection(): string {
    return this.direction;
  }

  /**
   * Get previous direction
   */
  getPreviousDirection(): string {
    return this.previousDirection;
  }

  /**
   * Get next intersection
   */
  getNextIntersection(): Point | null {
    return this.nextIntersection;
  }

  /**
   * Set next intersection
   */
  setNextIntersection(intersection: Point | null): void {
    this.nextIntersection = intersection;
  }

  /**
   * Set has respawned flag
   */
  setHasRespawned(value: boolean): void {
    this.hasRespawned = value;
  }

  /**
   * Reset movement state
   */
  reset(direction: string, previousDirection: string): void {
    this.direction = direction;
    this.previousDirection = previousDirection;
    this.nextIntersection = null;
    this.hasRespawned = false;
  }

  /**
   * Check if moving in x direction
   */
  isMovingInxDirection(direction: string): boolean {
    return direction === "left" || direction === "right";
  }

  /**
   * Handle input for Pacman's direction changes
   */
  handleDirectionInput(): void {
    // Check if cursors are initialized
    if (!this.cursors) {
      return;
    }

    // Check for joystick input first (alternative input method)
    const sceneWithJoystick = this.scene as Phaser.Scene & {
      joystickInput?: string | null;
    };
    if (
      sceneWithJoystick.joystickInput &&
      sceneWithJoystick.joystickInput !== this.direction
    ) {
      const joystickDirection = sceneWithJoystick.joystickInput;

      this.previousDirection = this.direction;
      this.direction = joystickDirection;
      this.nextIntersection = this.getNextIntersectionInNextDirection(
        this.pacman.x,
        this.pacman.y,
        this.previousDirection,
        joystickDirection
      );

      // Clear the joystick input after processing
      sceneWithJoystick.joystickInput = null;
      return;
    }

    const arrowKeys = [
      DIRECTIONS.LEFT,
      DIRECTIONS.RIGHT,
      DIRECTIONS.UP,
      DIRECTIONS.DOWN,
    ];
    for (const key of arrowKeys) {
      const cursorKey =
        this.cursors[key as keyof Phaser.Types.Input.Keyboard.CursorKeys];
      if (
        (cursorKey && cursorKey.isDown && this.direction !== key) ||
        this.hasRespawned
      ) {
        if (this.hasRespawned) this.hasRespawned = !this.hasRespawned;

        this.previousDirection = this.direction;
        this.direction = key;
        this.nextIntersection = this.getNextIntersectionInNextDirection(
          this.pacman.x,
          this.pacman.y,
          this.previousDirection,
          key
        );
        break;
      }
    }
  }

  /**
   * Find the next intersection that allows movement in the desired direction
   */
  getNextIntersectionInNextDirection(
    currentX: number,
    currentY: number,
    currentDirection: string,
    nextDirection: string
  ): Intersection | null {
    const intersections = this.getIntersections();
    const isUp = currentDirection === DIRECTIONS.UP;
    const isDown = currentDirection === DIRECTIONS.DOWN;
    const isLeft = currentDirection === DIRECTIONS.LEFT;
    const isRight = currentDirection === DIRECTIONS.RIGHT;
    const filteredIntersections = intersections
      .filter((intersection) => {
        return (
          ((isUp &&
            intersection.x === currentX &&
            intersection.y <= currentY) ||
            (isDown &&
              intersection.x === currentX &&
              intersection.y >= currentY) ||
            (isLeft &&
              intersection.y === currentY &&
              intersection.x <= currentX) ||
            (isRight &&
              intersection.y === currentY &&
              intersection.x >= currentX)) &&
          this.isIntersectionInDirection(intersection, nextDirection)
        );
      })
      .sort((a, b) => {
        if (isUp || isDown) {
          return isUp ? b.y - a.y : a.y - b.y;
        } else {
          return isLeft ? b.x - a.x : a.x - b.x;
        }
      });
    return filteredIntersections ? filteredIntersections[0] : null;
  }

  /**
   * Check if an intersection allows movement in a specific direction
   */
  isIntersectionInDirection(
    intersection: Intersection,
    direction: string
  ): boolean {
    switch (direction) {
      case DIRECTIONS.UP:
        return intersection.openPaths.includes(DIRECTIONS.UP);
      case DIRECTIONS.DOWN:
        return intersection.openPaths.includes(DIRECTIONS.DOWN);
      case DIRECTIONS.LEFT:
        return intersection.openPaths.includes(DIRECTIONS.LEFT);
      case DIRECTIONS.RIGHT:
        return intersection.openPaths.includes(DIRECTIONS.RIGHT);
      default:
        return false;
    }
  }

  /**
   * Handle Pacman's movement based on current direction
   */
  handlePacmanMovement(): void {
    if (!this.isActive) return;

    let nextIntersectionx = 0;
    let nextIntersectiony = 0;
    if (this.nextIntersection) {
      nextIntersectionx = this.nextIntersection.x;
      nextIntersectiony = this.nextIntersection.y;
    }
    switch (this.direction) {
      case DIRECTIONS.LEFT:
        this.handleMovementInDirection(
          DIRECTIONS.LEFT,
          DIRECTIONS.RIGHT,
          this.pacman.y,
          nextIntersectiony,
          this.pacman.x,
          true,
          false,
          0,
          -this.speed,
          0,
          this.pacman.body?.velocity.y
        );
        break;
      case DIRECTIONS.RIGHT:
        this.handleMovementInDirection(
          DIRECTIONS.RIGHT,
          DIRECTIONS.LEFT,
          this.pacman.y,
          nextIntersectiony,
          this.pacman.x,
          true,
          false,
          180,
          this.speed,
          0,
          this.pacman.body?.velocity.y
        );
        break;
      case DIRECTIONS.UP:
        this.handleMovementInDirection(
          DIRECTIONS.UP,
          DIRECTIONS.DOWN,
          this.pacman.x,
          nextIntersectionx,
          this.pacman.y,
          false,
          true,
          -90,
          0,
          -this.speed,
          this.pacman.body?.velocity.x
        );
        break;
      case DIRECTIONS.DOWN:
        this.handleMovementInDirection(
          DIRECTIONS.DOWN,
          DIRECTIONS.UP,
          this.pacman.x,
          nextIntersectionx,
          this.pacman.y,
          false,
          true,
          90,
          0,
          this.speed,
          this.pacman.body?.velocity.x
        );
        break;
    }
  }

  /**
   * Handle movement logic for a specific direction
   */
  private handleMovementInDirection(
    currentDirection: string,
    oppositeDirection: string,
    pacmanPosition: number = 0,
    intersectionPosition: number = 0,
    movingCoordinate: number,
    flipX: boolean,
    flipY: boolean,
    angle: number,
    velocityX: number,
    velocityY: number,
    currentVelocity?: number
  ): void {
    const perpendicularDirection =
      currentDirection === DIRECTIONS.LEFT ||
      currentDirection === DIRECTIONS.RIGHT
        ? [DIRECTIONS.UP, DIRECTIONS.DOWN]
        : [DIRECTIONS.LEFT, DIRECTIONS.RIGHT];
    let condition = false;
    if (this.nextIntersection)
      condition =
        (this.previousDirection == perpendicularDirection[0] &&
          pacmanPosition <= intersectionPosition) ||
        (this.previousDirection == perpendicularDirection[1] &&
          pacmanPosition >= intersectionPosition) ||
        this.previousDirection === oppositeDirection;
    if (condition) {
      const newPosition = intersectionPosition;
      if (
        this.previousDirection != oppositeDirection &&
        newPosition !== pacmanPosition
      ) {
        if (
          currentDirection === DIRECTIONS.LEFT ||
          currentDirection === DIRECTIONS.RIGHT
        )
          this.pacman.body?.reset(movingCoordinate, newPosition);
        else this.pacman.body?.reset(newPosition, movingCoordinate);
      }
      this.changeDirection(flipX, flipY, angle, velocityX, velocityY);
      this.adjustPacmanPosition(velocityX, velocityY);
    } else if (currentVelocity === 0) {
      this.changeDirection(flipX, flipY, angle, velocityX, velocityY);
      this.adjustPacmanPosition(velocityX, velocityY);
    }
  }

  /**
   * Adjust Pacman's position to align with the grid when changing direction
   */
  private adjustPacmanPosition(velocityX: number, velocityY: number): void {
    if (this.pacman.x % this.blockSize !== 0 && velocityY > 0) {
      const nearestMultiple =
        Math.round(this.pacman.x / this.blockSize) * this.blockSize;
      this.pacman.body?.reset(nearestMultiple, this.pacman.y);
    }
    if (this.pacman.y % this.blockSize !== 0 && velocityX > 0) {
      const nearestMultiple =
        Math.round(this.pacman.y / this.blockSize) * this.blockSize;
      this.pacman.body?.reset(this.pacman.x, nearestMultiple);
    }
  }

  /**
   * Apply visual and physics changes when changing direction
   */
  private changeDirection(
    flipX: boolean,
    flipY: boolean,
    angle: number,
    velocityX: number,
    velocityY: number
  ): void {
    this.pacman.setFlipX(flipX);
    this.pacman.setFlipY(flipY);
    this.pacman.setAngle(angle);
    this.pacman.setVelocityY(velocityY);
    this.pacman.setVelocityX(velocityX);
  }

  /**
   * Handle Pacman teleporting across the left and right edges of the maze
   */
  teleportPacmanAcrossWorldBounds(): void {
    const worldBounds = this.scene.physics.world.bounds;
    if (this.pacman.x <= worldBounds.x) {
      this.pacman.body?.reset(
        worldBounds.right - this.blockSize,
        this.pacman.y
      );
      this.nextIntersection = this.getNextIntersectionInNextDirection(
        this.pacman.x,
        this.pacman.y,
        DIRECTIONS.LEFT,
        this.direction
      );
      this.pacman.setVelocityX(-1 * this.speed);
    }
    if (this.pacman.x >= worldBounds.right) {
      this.pacman.body?.reset(worldBounds.x + this.blockSize, this.pacman.y);
      this.nextIntersection = this.getNextIntersectionInNextDirection(
        this.pacman.x,
        this.pacman.y,
        DIRECTIONS.RIGHT,
        this.direction
      );
      this.pacman.setVelocityX(this.speed);
    }
  }

  /**
   * Set is active flag
   */
  setIsActive(value: boolean): void {
    this.isActive = value;
  }
}
